import { useContext, useEffect, useRef } from "react";
import AppContext from "../../../contexts/AppContext";
import ColorAnalyzer from "./ColorAnalyzer";
import { Button } from "antd";
import { analyzeImageForRetrace } from "../../../lib/firebase/imageAnalysis";
import { processImageWithAI } from "../../../lib/firebase/colorDetection";
import { getTutorialByFileName, pinTutorialProcessing } from "../../../tutorials";

// Feature flag: use AI color detection instead of legacy k-means
const USE_AI_COLOR_DETECTION = true;

// Helper function to process the image
export const processAndAnalyzeImage = (image64, onStepChange, options = {}) => {
  return new Promise((resolve, reject) => {
    console.log('🔄 processAndAnalyzeImage: Starting with image64 length:', image64?.length);
    
    // Report upload step
    if (onStepChange) onStepChange("upload");
    
    let targetImg = image64;
    if (!targetImg) {
      console.log('⚠️ processAndAnalyzeImage: No image64 provided, trying SVG container');
      const svgContainer = document.getElementById("svgContainer");
      if (!svgContainer) {
        reject(new Error("No image data provided and SVG container not found"));
        return;
      }
      
      try {
        const svgXml = new XMLSerializer().serializeToString(svgContainer);
        targetImg = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgXml)))}`;
      } catch (error) {
        reject(new Error(`Failed to serialize SVG container: ${error.message}`));
        return;
      }
    }

    const img = new Image();
    img.onload = async () => {
      console.log('🖼️ processAndAnalyzeImage: Image loaded successfully');
      console.log('🖼️ processAndAnalyzeImage: Image dimensions:', img.width, 'x', img.height);
      
      // Create a canvas to draw the image
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width);
      canvas.height = Math.round(img.height);
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      // Check if image is low-res (will trigger auto-retrace)
      let needsRetrace = false;
      let retraceReason = "";
      
      if (canvas.width * canvas.height < 300 * 300) {
        needsRetrace = true;
        retraceReason = "very-low";
      } else if (canvas.width * canvas.height < 600 * 600) {
        needsRetrace = true;
        retraceReason = "low";
      }

      const imageData = targetImg;
      const dimensions = [
        img.width > 400 ? img.width : img.width * 2,
        img.height > 400 ? img.height : img.height * 2,
      ];

      const result = {
        file: imageData,
        originalDimensions: dimensions,
        largeImageWarning: img.width * img.height > 2073600,
        crop: {
          unit: "px",
          x: 0,
          y: 0,
          width: img.width,
          height: img.height,
        },
        imageDimensions: [img.width, img.height],
        despeckle: img.width * 0.01,
        wasUpscaled: needsRetrace,
        upscaleReason: retraceReason,
      };

      // Report colors step
      if (onStepChange) onStepChange("colors");

      // Color detection - AI or legacy
      const detectColors = async () => {
        if (USE_AI_COLOR_DETECTION) {
          console.log('🎨 processAndAnalyzeImage: Using AI color detection...');
          try {
            const hexColors = await processImageWithAI(img.src);
            console.log('🎨 processAndAnalyzeImage: AI found colors:', hexColors);
            return hexColors;
          } catch (aiError) {
            console.warn('🎨 processAndAnalyzeImage: AI color detection failed, falling back to legacy:', aiError);
            const hexColors = await ColorAnalyzer.processImage(img.src);
            return hexColors;
          }
        } else {
          const hexColors = await ColorAnalyzer.processImage(img.src);
          return hexColors;
        }
      };

      const tutorial = getTutorialByFileName(options.fileName);
      if (tutorial?.fixture?.colors) {
        console.log('🎯 processAndAnalyzeImage: using pinned tutorial palette for', options.fileName);
        resolve(pinTutorialProcessing(options.fileName, result));
        return;
      }

      detectColors()
        .then((hexColors) => {
          result.colors = hexColors.map((color) => [color.replace('#', ''), true]);
          console.log('✅ processAndAnalyzeImage: Color detection complete');
          resolve(result);
        })
        .catch((error) => {
          console.error("❌ processAndAnalyzeImage: Error detecting colors:", error);
          reject(error);
        });
    };

    img.onerror = (event) => {
      console.error("❌ processAndAnalyzeImage: Failed to load image");
      reject(event);
    };

    img.src = targetImg;
  });
};

const AutoUploadImage = (props) => {
  const { image64, fileName } = useContext(AppContext);
  
  // Prevent double processing
  const processingRef = useRef(false);

  // Check for tutorial processed image on mount
  useEffect(() => {
    if (window.tutorialProcessedImage && props.setFile) {
      console.log('🎯 AutoUploadImage: Found tutorial processed image on mount, applying...');
      const result = window.tutorialProcessedImage;
      
      props.setFile(result.file);
      props.setOriginalDimensions(result.originalDimensions);
      props.setLargeImageWarning(result.largeImageWarning);
      props.setCrop(result.crop);
      
      if (props.setImageDimensions) {
        props.setImageDimensions(result.imageDimensions);
      }
      
      if (props.setDespeckle) {
        props.setDespeckle(result.despeckle);
      }
      
      props.setColors(result.colors);
      window.tutorialProcessedImage = null;
    }
  }, []);

  const handleClick = async () => {
    // Prevent double processing
    if (processingRef.current) {
      console.log('⚠️ AutoUploadImage: Already processing, skipping...');
      return;
    }
    
    try {
      processingRef.current = true;
      console.log('🎯 AutoUploadImage: handleClick triggered');
      
      if (!image64) {
        console.error('❌ AutoUploadImage: No image64 data available in context!');
        processingRef.current = false;
        return;
      }
      
      // Step change callback for processing overlay
      const onStepChange = (step) => {
        if (props.setProcessingStep) {
          props.setProcessingStep(step);
        }
      };
      
      // Process image with step reporting (upload → colors)
      const result = await processAndAnalyzeImage(image64, onStepChange, { fileName });
      console.log("🎯 AutoUploadImage: Process result:", result);

      const tutorial = getTutorialByFileName(fileName);
      
      // Run AI analysis BEFORE setting file (because setFile unmounts this component!)
      onStepChange("analyze");
      
      let aiNeedsRetrace = false;
      let aiRetraceReason = "";
      if (tutorial) {
        console.log('🎯 AutoUploadImage: skipping retrace for pinned tutorial design');
      } else {
        try {
          console.log('🤖 AutoUploadImage: Running AI analysis...');
          const aiAnalysis = await analyzeImageForRetrace(image64);
          console.log('🤖 AutoUploadImage: AI Analysis result:', aiAnalysis);

          if (aiAnalysis.needsRetrace) {
            aiNeedsRetrace = true;
            aiRetraceReason = aiAnalysis.hasGradients ? 'gradients' : 'ai-analysis';
          }

          if (props.setAiAnalysisResult) {
            props.setAiAnalysisResult(aiAnalysis);
          }
        } catch (aiError) {
          console.warn('🤖 AutoUploadImage: AI analysis failed:', aiError);
        }
      }

      // Show "Ready!" state briefly
      onStepChange("ready");

      const needsRetrace = tutorial ? false : (aiNeedsRetrace || result.wasUpscaled);
      const retraceReason = tutorial ? undefined : (aiNeedsRetrace ? aiRetraceReason : result.upscaleReason);
      
      console.log('🎯 AutoUploadImage: Final retrace decision:', needsRetrace, 'reason:', retraceReason);
      
      // Small delay to show "Ready!" state
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // NOW set all converter state at once (this will unmount this component)
      // Set wasUpscaled BEFORE setFile so AIToolkitBar sees the correct value on mount
      if (props.setWasUpscaled) {
        props.setWasUpscaled(needsRetrace);
      }
      
      if (props.setUpscaleReason) {
        props.setUpscaleReason(retraceReason);
      }
      
      props.setOriginalDimensions(result.originalDimensions);
      props.setLargeImageWarning(result.largeImageWarning);
      props.setCrop(result.crop);

      if (props.setImageDimensions) {
        props.setImageDimensions(result.imageDimensions);
      }

      if (props.setDespeckle) {
        props.setDespeckle(result.despeckle);
      }

      props.setColors(result.colors);
      
      // Set file LAST - this triggers converter to open and unmounts this component
      props.setFile(result.file);
      
    } catch (error) {
      console.error("Error processing image:", error);
      // Reset processing step on error
      if (props.setProcessingStep) {
        props.setProcessingStep(null);
      }
    } finally {
      processingRef.current = false;
    }
  };

  return (
    <Button id="AutoConvertUpload" hidden onClick={handleClick}>
      Process Image
    </Button>
  );
};

export default AutoUploadImage;
